import { useInView } from "framer-motion";
import { useEffect, useMemo, useRef } from "react";

interface UseScrollAnimationOptions {
	threshold?: number;
	triggerOnce?: boolean;
	margin?: string;
	// Add optimization options
	reducedMotion?: boolean;
}

export function useScrollAnimation<T extends HTMLElement>(options: UseScrollAnimationOptions = {}) {
	const ref = useRef<T>(null);

	// Optimize thresholds - use larger values to reduce callback frequency
	const optimizedThreshold = useMemo(() => {
		const threshold = options.threshold ?? 0.2; // Increased from 0.1
		// Clamp threshold between 0.1 and 0.8 for better performance
		return Math.max(0.1, Math.min(0.8, threshold));
	}, [options.threshold]);

	// Use more conservative margin for better performance
	const optimizedMargin = useMemo(() => {
		return options.margin || "-5% 0px -5% 0px"; // Less aggressive preloading
	}, [options.margin]) as
		| `${number}${"px" | "%"}`
		| `${number}${"px" | "%"} ${number}${"px" | "%"}`
		| `${number}${"px" | "%"} ${number}${"px" | "%"} ${number}${"px" | "%"}`
		| `${number}${"px" | "%"} ${number}${"px" | "%"} ${number}${"px" | "%"} ${number}${"px" | "%"}`;

	// Check for reduced motion preference
	const prefersReducedMotion = useMemo(() => {
		if (typeof window === "undefined") return false;
		return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	}, []);

	const isInView = useInView(ref, {
		once: options.triggerOnce ?? true, // Default to triggerOnce for better performance
		amount: optimizedThreshold,
		margin: optimizedMargin,
	});

	// Simple will-change management - only when in view
	useEffect(() => {
		const element = ref.current;
		if (!element || prefersReducedMotion || options.reducedMotion) return;

		if (isInView) {
			element.style.willChange = "transform, opacity";

			// Remove will-change after animation
			const cleanup = setTimeout(() => {
				if (element) {
					element.style.willChange = "auto";
				}
			}, 1000);

			return () => clearTimeout(cleanup);
		}
	}, [isInView, prefersReducedMotion, options.reducedMotion]);

	return {
		ref,
		isInView: prefersReducedMotion || options.reducedMotion ? true : isInView, // Skip animations if reduced motion is preferred
	};
}
